import React from "react";
import { Badge, Button, Card, IndexTable } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import StorePageFrame from "../userCustomDashboard/emLayout/StorePageFrame";
import {
  formatCount,
  formatDateTime,
  formatDuration,
  formatPercent,
  readableStatus,
  statusTone,
} from "../support/supportFormatters";
import { useStoreWorkspace } from "./StoreWorkspaceContext";
import WorkspaceState from "./WorkspaceState";
import { workspaceModel } from "./workspaceModel";
import "./style.css";

const StoreSyncActivity = () => {
  const navigate = useNavigate();
  const workspace = useStoreWorkspace();
  const model = workspaceModel(workspace.data, workspace.shopId);
  const { pipeline, recentActivity, recentOrders, lifecycle, disputes } = model;
  const stages = [
    ["Received · 1h", pipeline.received1h],
    ["Received · 24h", pipeline.received24h],
    ["Queued", pipeline.queued],
    ["Processing", pipeline.processing],
    ["Retry wait", pipeline.retryWait],
    ["Manual review", pipeline.manualReview],
    ["Terminal / blocked", pipeline.blocked],
    ["Succeeded · 24h", pipeline.success24h],
    ["Failed · 24h", pipeline.failed24h],
  ];

  const rows = recentActivity.map((event, index) => (
    <IndexTable.Row id={String(event?.id || index)} key={String(event?.id || index)} position={index}>
      <IndexTable.Cell>{formatDateTime(event?.occurredAt || event?.createdAt || event?.timestamp)}</IndexTable.Cell>
      <IndexTable.Cell>{event?.type || event?.eventType || "Sync activity"}</IndexTable.Cell>
      <IndexTable.Cell>{event?.message || event?.description || "No details returned"}</IndexTable.Cell>
      <IndexTable.Cell>{event?.reason || "—"}</IndexTable.Cell>
    </IndexTable.Row>
  ));
  const orderRows = recentOrders.map((order, index) => (
    <IndexTable.Row id={String(order?.fulfillmentId || index)} key={String(order?.fulfillmentId || index)} position={index}>
      <IndexTable.Cell><Badge status={statusTone(order?.status)}>{readableStatus(order?.status)}</Badge></IndexTable.Cell>
      <IndexTable.Cell>{order?.orderName || order?.storeOrderId || "Unknown"}</IndexTable.Cell>
      <IndexTable.Cell>
        {order?.carrier || "Carrier not recorded"}
        {order?.trackingNumber ? <span className="store-support-cell-note">{order.trackingNumber}</span> : null}
        {order?.transactionId ? <span className="store-support-cell-note">Transaction {order.transactionId}</span> : null}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {formatDateTime(order?.receivedAt)}
        {order?.processedAt ? <span className="store-support-cell-note">Processed {formatDateTime(order.processedAt)}</span> : null}
      </IndexTable.Cell>
      <IndexTable.Cell>{formatDuration(order?.syncLatencyMs)}</IndexTable.Cell>
      <IndexTable.Cell>
        {order?.statusMessage || "No status message"}
        {order?.processingMethod ? (
          <span className="store-support-cell-note">Method {readableStatus(order.processingMethod)}</span>
        ) : null}
        {order?.source ? (
          <span className="store-support-cell-note">Source {readableStatus(order.source)}</span>
        ) : null}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <StorePageFrame
      title="Sync activity"
      maxWidth="1400px"
      primaryAction={{ content: "Open orders", onAction: () => navigate(`/store/${workspace.shopId}/orders`) }}
    >
      <WorkspaceState
        loading={workspace.loading}
        refreshing={workspace.refreshing}
        error={workspace.error}
        hasData={Boolean(workspace.data)}
        onRetry={() => workspace.refresh()}
      >
        <Card>
          <div className="store-support-card">
            <div className="store-support-heading store-support-heading--action">
              <div>
                <h2>Freshness and store clocks</h2>
                <p>Use these timestamps to distinguish an idle merchant from a stalled ingestion or PayPal delivery path.</p>
              </div>
              <Button onClick={() => workspace.refresh()}>Refresh evidence</Button>
            </div>
            <dl className="store-support-definition-grid store-support-definition-grid--four">
              <div className="store-support-definition"><dt>Evidence observed</dt><dd>{formatDateTime(model.observedAt)}</dd></div>
              <div className="store-support-definition"><dt>Merchant activity</dt><dd>{formatDateTime(lifecycle.lastActivityAt)}</dd></div>
              <div className="store-support-definition"><dt>Fulfillment pull</dt><dd>{formatDateTime(lifecycle.lastProcessedAtPullAt)}</dd><small>{readableStatus(lifecycle.lastPullStatus, "No pull status")}</small></div>
              <div className="store-support-definition"><dt>Last successful sync</dt><dd>{formatDateTime(lifecycle.lastSyncedAt || pipeline.lastSuccessfulAt)}</dd></div>
              <div className="store-support-definition"><dt>Oldest queued item</dt><dd>{formatDateTime(pipeline.oldestQueuedAt, pipeline.queued === 0 ? "Queue is empty" : "Not recorded")}</dd></div>
              <div className="store-support-definition"><dt>Dispute refresh</dt><dd>{formatDateTime(disputes.lastSyncedAt)}</dd><small>{readableStatus(disputes.syncStatus, "Not started")}</small></div>
            </dl>
          </div>
        </Card>
        <div className="store-support-kpis store-support-kpis--sync">
          {stages.map(([label, value]) => (
            <Card key={label}><div><span>{label}</span><strong>{formatCount(value)}</strong></div></Card>
          ))}
        </div>
        <Card>
          <div className="store-support-card">
            <div className="store-support-heading"><h2>Performance</h2><p>End-to-end time from Shopify receipt to a terminal PayPal result.</p></div>
            <dl className="store-support-definition-grid store-support-definition-grid--four">
              <div className="store-support-definition"><dt>Success · 1h</dt><dd>{formatPercent(pipeline.successRate1h)}</dd></div>
              <div className="store-support-definition"><dt>Success · 24h</dt><dd>{formatPercent(pipeline.successRate24h)}</dd></div>
              <div className="store-support-definition"><dt>P50 sync</dt><dd>{formatDuration(pipeline.p50SyncMs)}</dd></div>
              <div className="store-support-definition"><dt>P95 sync</dt><dd>{formatDuration(pipeline.p95SyncMs)}</dd></div>
              <div className="store-support-definition"><dt>P99 sync</dt><dd>{formatDuration(pipeline.p99SyncMs)}</dd></div>
              <div className="store-support-definition"><dt>Throughput</dt><dd>{formatCount(pipeline.throughputPerMinute)} / min</dd></div>
            </dl>
          </div>
        </Card>
        <Card>
          <div className="store-support-table-heading">
            <div><h2>Recent orders</h2><p>Latest fulfillment outcomes and measured sync time.</p></div>
          </div>
          <IndexTable
            resourceName={{ singular: "order", plural: "orders" }}
            itemCount={recentOrders.length}
            selectable={false}
            headings={[
              { title: "Status" },
              { title: "Order" },
              { title: "Tracking" },
              { title: "Received" },
              { title: "Sync time" },
              { title: "Details" },
            ]}
          >{orderRows}</IndexTable>
          {recentOrders.length === 0 ? <div className="store-support-table-empty">No recent orders were returned.</div> : null}
        </Card>
        <Card>
          <div className="store-support-table-heading">
            <div><h2>Recent sync activity</h2><p>Newest state changes returned by the aggregate workspace.</p></div>
            <Button onClick={() => navigate(`/store/${workspace.shopId}/events`)}>Open diagnostics</Button>
          </div>
          <IndexTable
            resourceName={{ singular: "event", plural: "events" }}
            itemCount={recentActivity.length}
            selectable={false}
            headings={[
              { title: "Time" },
              { title: "Event" },
              { title: "Details" },
              { title: "Reason" },
            ]}
          >{rows}</IndexTable>
          {recentActivity.length === 0 ? <div className="store-support-table-empty">No recent sync activity was returned.</div> : null}
        </Card>
      </WorkspaceState>
    </StorePageFrame>
  );
};

export default StoreSyncActivity;
